import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { speak, cancelSpeech } from "@/lib/speak";
import { ZOOD_MASCOT } from "@/lib/brand";
import { BookOpen, Play, CheckCircle2, Star, Volume2, ArrowRight, X, HelpCircle } from "lucide-react";

const seenKey = (uid) => `zl_student_guide_${uid}`;

// Picture-first, read-aloud walkthrough for young learners. Auto-reads each card
// and offers a speaker button; shows once on first login with a replay button.
const CARDS = [
  { mascot: true, text: "Welcome! Let's learn together.", color: "#7A3FF2" },
  { Icon: BookOpen, text: "Pick a subject to start.", color: "#0284C7" },
  { Icon: Play, text: "Tap a lesson to play.", color: "#0D9488" },
  { Icon: CheckCircle2, text: "Answer, then tap Next.", color: "#10B981" },
  { Icon: Star, text: "Win points and stickers!", color: "#FF9E1B" },
  { Icon: Volume2, text: "Tap the speaker to hear it.", color: "#E05A47" },
];

export default function StudentGuide() {
  const { user, role } = useAuth();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);

  useEffect(() => {
    if (!user || role !== "student") return;
    if (!localStorage.getItem(seenKey(user.id))) { setI(0); setOpen(true); }
  }, [user, role]);

  // read the current card aloud whenever it changes / opens
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => speak(CARDS[i].text, "nova", { speed: 0.9 }), 250);
    return () => { clearTimeout(t); cancelSpeech(); };
  }, [open, i]);

  if (!user || role !== "student") return null;

  const card = CARDS[i];
  const isLast = i === CARDS.length - 1;
  const close = () => { cancelSpeech(); if (user) localStorage.setItem(seenKey(user.id), "1"); setOpen(false); };
  const next = () => (isLast ? close() : setI((v) => v + 1));

  return (
    <>
      <button
        onClick={() => { setI(0); setOpen(true); }}
        data-testid="student-guide-replay"
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-[#FF9E1B] px-4 py-3 text-sm font-extrabold text-white shadow-lg transition-transform hover:scale-105 active:scale-95"
      >
        <HelpCircle size={18} /> How to play
      </button>

      {open && createPortal(
        <AnimatePresence>
          <motion.div
            className="fixed inset-0 z-[85] flex items-center justify-center p-4"
            style={{ background: `${card.color}` }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="student-guide"
          >
            <button onClick={close} data-testid="student-guide-close" className="absolute right-5 top-5 rounded-full bg-white/25 p-2 text-white hover:bg-white/40" aria-label="Close">
              <X size={22} />
            </button>

            <motion.div
              key={i}
              initial={{ scale: 0.85, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className="flex w-full max-w-sm flex-col items-center text-center"
              data-testid={`student-guide-card-${i}`}
            >
              <motion.div
                animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
                className="flex h-40 w-40 items-center justify-center rounded-full bg-white/95 shadow-2xl"
              >
                {card.mascot
                  ? <img src={ZOOD_MASCOT} alt="" className="h-28 w-28 object-contain" />
                  : <card.Icon size={72} strokeWidth={2.4} style={{ color: card.color }} />}
              </motion.div>

              <h2 className="mt-8 font-heading text-3xl font-extrabold leading-tight text-white drop-shadow-sm">{card.text}</h2>

              <button
                onClick={() => speak(card.text, "nova", { speed: 0.9 })}
                data-testid="student-guide-speak"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/25 px-5 py-2.5 font-bold text-white hover:bg-white/40"
              >
                <Volume2 size={18} /> Hear it again
              </button>

              <div className="mt-8 flex items-center justify-center gap-2">
                {CARDS.map((_, k) => (
                  <span key={k} className="h-2.5 rounded-full bg-white transition-all" style={{ width: k === i ? 26 : 9, opacity: k === i ? 1 : 0.5 }} />
                ))}
              </div>

              <button
                onClick={next}
                data-testid="student-guide-next"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-9 py-4 font-heading text-lg font-extrabold shadow-xl transition-transform hover:scale-105 active:scale-95"
                style={{ color: card.color }}
              >
                {isLast ? "Let's go!" : "Next"} <ArrowRight size={22} />
              </button>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
